/**
 * Build an Unsplash image URL that cannot forget to ask for a size.
 *
 * WHY THIS EXISTS:
 *
 * The home page hero was hand-written without a `w=` parameter. Unsplash's default when you omit it
 * is the photo's NATIVE resolution, so the page shipped a **2,026,554 byte** JPEG — over 75% of the
 * page's total weight — for a layer rendered at `opacity-15` behind the headline. Nothing about the
 * URL looked wrong; the missing parameter is invisible until someone weighs the response. Two more
 * URLs (`/login`, `/register`) had the same omission.
 *
 * Width is therefore REQUIRED here, and `auto=format` is always on so browsers negotiate AVIF or
 * WebP instead of JPEG. The same hero through this helper is ~456 KB — a 78% saving for an image
 * nobody looks at directly.
 *
 * `q` defaults deliberately low. These are photographic backgrounds, most of them behind copy or at
 * low opacity; the artefacts that would show on a product shot are invisible here, and quality is
 * the cheapest byte to give back. Raise it per-call if an image is ever shown at full strength.
 */

const BASE = 'https://images.unsplash.com';

/**
 * @param {string} photoId  the `photo-…` segment of an Unsplash URL
 * @param {{ w: number, q?: number, fit?: string }} opts  `w` is required — see above
 */
export function unsplash(photoId, { w, q = 70, fit = 'crop' }) {
  if (!w) {
    // Loud rather than silent: falling back to a default here would hide exactly the mistake this
    // helper exists to prevent, and the cost only shows up in someone's data bill.
    throw new Error(`unsplash(${photoId}): a width is required — omitting it serves the full-resolution original`);
  }

  const params = new URLSearchParams({
    crop: 'entropy',
    cs: 'srgb',
    auto: 'format',
    fit,
    w: String(w),
    q: String(q),
  });

  return `${BASE}/${photoId}?${params.toString()}`;
}
